import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { setCitiesBalances, confirmTransferMoney } from '../../actions/bank';
import { filterData } from '../../actions/filterData';
import { useTranslation } from 'react-i18next';
import usePhoneCheck from '../../hooks/usePhoneCheck';
import BankMobileBody from '../../components/ComponentTable/Table/BankMobileBody';
import CustomAutocomplete from '../../components/CustomAutocomplete';
import BankTableBody from '../../components/ComponentTable/Table/BankTableBody';
import BankTableHead from '../../components/ComponentTable/Table/BankTableHead';
import BankTableToolbar from '../../components/ComponentTable/Table/BankTableToolbar';
import { Stack, Box, Table, TableContainer, Paper } from '@mui/material';
import { toast } from 'react-toastify';
import BackButton from '../../components/BalanceComponent/BackButton';

const headCells = [
    {
        id: 'checkbox',
        numeric: false,
        disablePadding: false,
        label: '',
        type: 'checkbox',
    },
    {
        id: 'rowId',
        numeric: false,
        disablePadding: false,
        label: 'bankFranchPage.cityId',
        type: 'string',
    },
    {
        id: 'city',
        numeric: false,
        disablePadding: false,
        label: 'bankFranchPage.city',
        type: 'string',
    },
    {
        id: 'surname',
        numeric: false,
        disablePadding: false,
        label: 'bankFranchPage.surname',
        type: 'string',
    },
    {
        id: 'name',
        numeric: false,
        disablePadding: false,
        label: 'bankFranchPage.name',
        type: 'string',
    },
    {
        id: 'balance',
        numeric: false,
        disablePadding: false,
        label: 'bankFranchPage.balance',
        type: 'string',
    },
    {
        id: 'cityStatus',
        numeric: false,
        disablePadding: false,
        label: 'bankFranchPage.status',
        type: 'cityStatus',
    },
    {
        id: 'quantity',
        numeric: true,
        disablePadding: false,
        label: 'bankFranchPage.quantity',
        type: 'decimalInput',
    },
    {
        id: 'bankAccount',
        numeric: true,
        disablePadding: false,
        label: 'bankFranchPage.bankAccount',
        type: 'link',
    },
];

function getRowData(data) {
    return data
        ? data.map(item => ({
              error: false,
              rowId: item.city_id,
              franchId: item.franch_id,
              name: item.franch_first_name || '',
              surname: item.franch_last_name || '',
              city: item.city_name.split('|||')[1] || '',
              balance: Number(
                  (Number(item.franch_balance) + Number(item.teach_balance) + Number(item.student_balance)).toFixed(2)
              ),
              quantity: 300,
              cityStatus: '',
          }))
        : [];
}

export default function CityBalances() {
    const [selected, setSelected] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errorStates, setErrorStates] = useState({});
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('');
    const [filters, setFilters] = useState({
        rowId: '',
        surname: '',
        city: '',
        cityStatus: '',
    });

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const mobile = usePhoneCheck();
    const history = useHistory();
    const { t } = useTranslation();
    const user = useSelector(state => state.bank);
    const balance = user.customer.balance || 0;

    const citiesBalancesInfo = useMemo(() => {
        return getRowData(user.customer.citiesBalances);
    }, [user]);

    useEffect(() => {
        setRows(citiesBalancesInfo);
    }, [citiesBalancesInfo]);

    const [rows, setRows] = useState(citiesBalancesInfo);
    const dispatch = useDispatch();

    const fetchCitiesBalances = async () => {
        await dispatch(setCitiesBalances());
    };

    useEffect(() => {
        fetchCitiesBalances();
    }, []);

    const handleQuantityChange = (id, value) => {
        const rowIndex = rows.findIndex(row => row.rowId === id);
        const newRows = [...rows];
        newRows[rowIndex] = {
            ...newRows[rowIndex],
            quantity: parseFloat(value) || '',
        };

        setRows(newRows);
        setErrorStates(prevState => ({
            ...prevState,
            [id]: value === '',
        }));
    };

    const handleSelectAllClick = event => {
        if (loading) return;
        if (event.target.checked) {
            const newSelected = rows.map(n => n.rowId);
            setSelected(newSelected);
            return;
        }
        setSelected([]);
    };

    const handleClick = (event, id) => {
        if (loading) return;

        const selectedIndex = selected.indexOf(id);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = [...selected, id];
        } else if (selectedIndex >= 0) {
            newSelected = selected.filter(itemId => itemId !== id);
        }

        setSelected(newSelected);
        setErrorStates(prevState => ({
            ...prevState,
            [id]: false,
        }));
    };

    const isSelected = id => selected.indexOf(id) !== -1;

    async function handleSuccessClick() {
        await confirmTransferMoney({
            rows,
            isSelected,
            toast,
            t,
            balance,
            updateBalanceFunction: fetchCitiesBalances,
            getCustomerId: el => el.franchId,
            setSelected,
        });
    }

    const handleFilterChange = (key, value) => {
        if (loading) return;
        const newFilters = { ...filters, [key]: value };
        setFilters(newFilters);
        setRows(filterData(citiesBalancesInfo, newFilters));
    };

    const filterConfigs = [
        { label: 'ID', key: 'rowId' },
        { label: t('bankFranchPage.surname'), key: 'surname' },
        { label: t('bankFranchPage.city'), key: 'city' },
        { label: t('bankFranchPage.status'), key: 'status' },
    ];

    const handleBankAccountClick = id => {
        history.push(`/users/bank/${id}/franch`);
    };

    return (
        <Box sx={{ width: '100%' }}>
            <BackButton />
            <Paper sx={{ width: '100%', mb: 2, p: 3 }}>
                <Stack direction={mobile ? 'column' : 'row'} justifyContent="flex-start" spacing={2}>
                    {filterConfigs.map(({ label, key, transform }) => (
                        <CustomAutocomplete
                            key={key}
                            label={label}
                            options={[...new Set(rows.map(row => (transform ? transform(row[key]) : row[key]) || ''))]}
                            inputValue={filters[key] || ''}
                            onChange={(e, newValue) => handleFilterChange(key, newValue || '')}
                            onInputChange={(e, newInputValue) =>
                                setFilters(prev => ({ ...prev, [key]: newInputValue || '' }))
                            }
                        />
                    ))}
                </Stack>
                <BankTableToolbar
                    title={t('bankFranchPage.selected')}
                    titleTooltipSuccess={t('bankStudentsPage.tooltipTitle')}
                    numSelected={selected.length}
                    onSuccessClick={handleSuccessClick}
                    loading={loading}
                    selectedUserIds={selected}
                    rows={rows}
                />
                {mobile ? (
                    <BankMobileBody
                        rows={rows}
                        headCells={headCells}
                        isSelected={isSelected}
                        handleSelectAllClick={handleSelectAllClick}
                        handleQuantityChange={handleQuantityChange}
                        handleClick={handleClick}
                        handleBankAccountClick={handleBankAccountClick}
                        mobile={mobile}
                    />
                ) : (
                    <TableContainer style={{ maxHeight: '59vh' }}>
                        <Table stickyHeader sx={{ minWidth: 750 }} aria-labelledby="tableTitle">
                            <BankTableHead
                                numSelected={selected.length}
                                onSelectAllClick={handleSelectAllClick}
                                headCells={headCells}
                                order={order}
                                orderBy={orderBy}
                                onRequestSort={handleRequestSort}
                            />
                            <BankTableBody
                                rows={rows}
                                headCells={headCells}
                                isSelected={isSelected}
                                handleClick={handleClick}
                                handleQuantityChange={handleQuantityChange}
                                handleBankAccountClick={handleBankAccountClick}
                                order={order}
                                orderBy={orderBy}
                            />
                        </Table>
                    </TableContainer>
                )}
            </Paper>
        </Box>
    );
}
